/**
 * فرمِ ویرایشِ یک منبع — که در واقع فرمِ **ثبتِ درخواستِ** ویرایش است.
 *
 * ⚠️ عمداً همه‌ی فیلدهای `Source` این‌جا نیستند. `follow`، `paginate`،
 * `image_rewrite` و `expect` ساختارِ تودرتو دارند و فرمِ درست‌شان یعنی یک
 * ویرایشگرِ کوچکِ اختصاصی برای هرکدام — کاری که تا وقتی کسی واقعاً از
 * این‌جا لازمشان نکرده، ارزشش را ندارد. همان‌ها از `crawl registry --edit`
 * قابلِ ویرایش‌اند و این صفحه مقادیرِ فعلی‌شان را **نشان** می‌دهد تا معلوم
 * باشد چه چیزی هست و دست‌نخورده می‌ماند.
 *
 * فقط فیلدهایی که واقعاً عوض شده‌اند فرستاده می‌شوند (`changedFields`) —
 * وگرنه کارِ صف‌شده چیزی می‌شود که نمی‌شود خواندش: «چه چیزی قرار است عوض
 * شود؟» باید از خودِ ردیفِ صف معلوم باشد.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sourceRepo } from '@/core/data/sourceRepo';
import {
  changedFields, makesNewVersion, validateEdit,
  EXTRACTOR_CHOICES, TIMEZONE_CHOICES,
} from '@/core/lib/sourcePipeline';
import { reportError } from '@/core/lib/report';

const TIERS = ['api', 'feed', 'scrape_ok', 'disallowed'];

const str = (v: unknown): string => (v == null ? '' : String(v));
const list = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

/** ساختارهای تودرتو فقط نشان داده می‌شوند؛ ویرایششان جای دیگری است. */
function ReadOnly({ label, value }: { label: string; value: unknown }) {
  if (value == null || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <p className="dim" style={{ fontSize: 12 }}>
      {label}: <span className="mono">{JSON.stringify(value)}</span>
    </p>
  );
}

export function SourceEditor({ sourceId, current, onDone }: {
  sourceId: string;
  current: Record<string, unknown>;
  onDone: (message: string) => void;
}) {
  const qc = useQueryClient();
  const [label, setLabel] = useState(str(current.label));
  const [tier, setTier] = useState(str(current.tier));
  const [extractor, setExtractor] = useState(str(current.extractor));
  const [trust, setTrust] = useState(str(current.trust));
  const [enabled, setEnabled] = useState(current.enabled === true);
  const [aggregator, setAggregator] = useState(current.aggregator === true);
  const [heuristic, setHeuristic] = useState(current.heuristic_detail === true);
  const [timezone, setTimezone] = useState(str(current.timezone));
  const [gooutSource, setGooutSource] = useState(str(current.goout_source));
  const [venue, setVenue] = useState(str(current.default_venue));
  const [venueAddress, setVenueAddress] = useState(str(current.default_venue_address));
  const [categories, setCategories] = useState(list(current.default_categories).join(', '));
  const [urls, setUrls] = useState(list(current.urls).join('\n'));
  const [notes, setNotes] = useState(str(current.notes));
  const [error, setError] = useState<string | null>(null);

  const build = (): Record<string, unknown> => ({
    label: label.trim(),
    tier,
    extractor: extractor.trim(),
    trust: trust.trim() === '' ? null : Number(trust),
    enabled,
    aggregator,
    heuristic_detail: heuristic,
    timezone: timezone.trim() || null,
    goout_source: gooutSource.trim() || null,
    default_venue: venue.trim() || null,
    default_venue_address: venueAddress.trim() || null,
    default_categories: categories.split(',').map((c) => c.trim()).filter(Boolean),
    urls: urls.split('\n').map((u) => u.trim()).filter(Boolean),
    notes: notes.trim() || null,
  });

  const save = useMutation({
    mutationFn: () => {
      const patch = changedFields(current, build());
      const check = validateEdit(current, patch);
      if (!check.ok) return Promise.reject(new Error(check.error));
      return sourceRepo.queue('edit_source', sourceId, patch)
        .then(() => Object.keys(patch));
    },
    onSuccess: (fields) => onDone(`ویرایشِ ${sourceId} در صف نشست: ${fields.join('، ')}`),
    onError: (err) => {
      reportError('admin.sources.edit', err);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  /**
   * «اول ببین جواب می‌دهد، بعد ثبت کن.»
   *
   * ⚠️ این دکمه دلیلِ وجودِ کلِ `p_settings` است. تا امروز تنها راهِ
   * امتحان کردنِ یک `extractor` تازه، ثبتِ یک نسخه‌ی رجیستری بود و دیدنِ
   * نتیجه‌اش فردا صبح — یعنی هر آزمایش یک نسخه‌ی بی‌دلیل در تاریخچه
   * می‌گذاشت. این‌جا همان تنظیمات به کرالر می‌رود، در حافظه اجرا می‌شود، و
   * هیچ نسخه‌ای ساخته نمی‌شود.
   *
   * نتیجه در پنلِ «تست»ِ همین ردیف می‌آید، پس پنجره بسته می‌شود؛ نشان
   * دادنِ گزارشِ شش‌قدمی داخلِ یک پنجره‌ی ۴۲۰ پیکسلی یعنی دو جای متفاوت
   * برای یک چیز.
   */
  const tryIt = useMutation({
    mutationFn: () => {
      const patch = changedFields(current, build());
      const check = Object.keys(patch).length > 0
        ? validateEdit(current, patch)
        : { ok: true as const };
      if (!check.ok) return Promise.reject(new Error(check.error));
      return sourceRepo.test(sourceId, Object.keys(patch).length > 0 ? patch : null);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['srcTestRuns', sourceId] });
      onDone(`تستِ ${sourceId} در صف نشست — نتیجه در بخشِ «تست» می‌آید.`);
    },
    onError: (err) => {
      reportError('admin.sources.editTest', err);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const patch = changedFields(current, build());
  const dirty = Object.keys(patch);

  return (
    <>
      <input className="input" value={label} placeholder="نامِ نمایشی"
        onChange={(e) => setLabel(e.target.value)} />

      <div className="filters" style={{ marginBottom: 8 }}>
        <label className="dim" htmlFor="ed-tier">tier</label>
        <select id="ed-tier" value={tier} onChange={(e) => setTier(e.target.value)}>
          {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label className="dim" htmlFor="ed-trust">trust</label>
        <input id="ed-trust" value={trust} dir="ltr" style={{ width: 70 }}
          onChange={(e) => setTrust(e.target.value)} />
      </div>

      {/* ⚠️ دراپ‌داون و نه ورودیِ آزاد: نامی که کرالر نشناسد یک منبعِ
          خاموشِ بی‌خطا می‌سازد — `unsupported-extractor` شمرده می‌شود و
          هیچ‌جا نمی‌گوید چرا. `tests/test_extractor_choices_match.py` در
          مخزنِ کرالر همین فهرست را با `EXTRACTORS` مقابله می‌کند. */}
      <div className="filters" style={{ marginBottom: 8 }}>
        <label className="dim" htmlFor="ed-ex">extractor</label>
        <select id="ed-ex" value={extractor} onChange={(e) => setExtractor(e.target.value)}>
          {EXTRACTOR_CHOICES.map((x) => (
            <option key={x.value} value={x.value}>{x.label}</option>
          ))}
        </select>
      </div>

      {/* غلطِ املایی این‌جا یعنی رخدادهایی که چند ساعت جابه‌جا منتشر
          می‌شوند، بی‌آنکه چیزی خطا بدهد. */}
      <div className="filters" style={{ marginBottom: 8 }}>
        <label className="dim" htmlFor="ed-tz">منطقه‌ی زمانی</label>
        <select id="ed-tz" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
          <option value="">— تعیین نشده —</option>
          {TIMEZONE_CHOICES.map((z) => <option key={z} value={z}>{z}</option>)}
        </select>
      </div>
      <input className="input" value={gooutSource} placeholder="goout_source" dir="ltr"
        onChange={(e) => setGooutSource(e.target.value)} />

      {/* یک آدرس در هر خط. دامنه‌شان باید همان دامنه‌ی فعلی بماند — سایتِ
          دیگر یعنی منبعِ دیگر، نه ویرایشِ این یکی. */}
      <textarea className="input textarea" value={urls} dir="ltr"
        placeholder="یک آدرس در هر خط" onChange={(e) => setUrls(e.target.value)} />

      <input className="input" value={categories} placeholder="دسته‌های پیش‌فرض (music/jazz، …)"
        dir="ltr" onChange={(e) => setCategories(e.target.value)} />
      <input className="input" value={venue} placeholder="سالنِ پیش‌فرض"
        onChange={(e) => setVenue(e.target.value)} />
      <input className="input" value={venueAddress} placeholder="نشانیِ سالنِ پیش‌فرض"
        onChange={(e) => setVenueAddress(e.target.value)} />
      <textarea className="input textarea" value={notes} placeholder="یادداشت"
        onChange={(e) => setNotes(e.target.value)} />

      <div className="chiprow" style={{ marginBottom: 8 }}>
        <label className="dim">
          <input type="checkbox" checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)} /> فعال
        </label>
        <label className="dim">
          <input type="checkbox" checked={aggregator}
            onChange={(e) => setAggregator(e.target.checked)} /> aggregator
        </label>
        <label className="dim">
          <input type="checkbox" checked={heuristic}
            onChange={(e) => setHeuristic(e.target.checked)} /> heuristic_detail
        </label>
      </div>

      <ReadOnly label="follow" value={current.follow} />
      <ReadOnly label="paginate" value={current.paginate} />
      <ReadOnly label="image_rewrite" value={current.image_rewrite} />
      <ReadOnly label="follow_domains" value={current.follow_domains} />
      <p className="dim" style={{ fontSize: 12 }}>
        شهر (<span className="mono">{str(current.city) || '—'}</span>) و شناسه از این‌جا
        عوض نمی‌شوند؛ شهر کلیدِ کلِ خطِ لوله است.
      </p>

      {error ? <p className="err">{error}</p> : null}

      <p className="dim" style={{ marginBottom: 6 }}>
        {dirty.length === 0 ? 'چیزی عوض نشده.' : `عوض می‌شود: ${dirty.join('، ')}`}
      </p>

      {/*
        «نسخه‌ی تازه می‌سازد یا نه» قبل از زدنِ دکمه گفته می‌شود، چون جوابش
        شهودی نیست: عوض کردنِ آدرس نسخه می‌سازد ولی عوض کردنِ trust نه.
        معیارش «آیا دادهٔ بیرون‌آمده عوض می‌شود؟» است، نه «آیا تنظیم است؟».
        و «نسخه‌ی تازه نه» یعنی همان ردیفِ فعال به‌روز می‌شود — نه اینکه
        هیچ اتفاقی نیفتد.
      */}
      {dirty.length > 0 ? (
        <p className="note">
          {makesNewVersion(patch)
            ? 'این تغییر رفتاری است: نسخه‌ی تازه ساخته می‌شود و نسخه‌ی فعلی'
              + ' بازنشسته می‌ماند (پاک نمی‌شود).'
            : 'این تغییر رفتاری نیست: نسخه‌ی تازه‌ای ساخته نمی‌شود و همین'
              + ' نسخه‌ی فعال به‌روز می‌شود.'}
        </p>
      ) : null}

      <button className="btn block" type="button" disabled={tryIt.isPending}
        onClick={() => { setError(null); tryIt.mutate(); }}>
        {tryIt.isPending ? '…' : (dirty.length > 0
          ? 'با همین تنظیمات تست کن (بدونِ ثبت)'
          : 'با تنظیماتِ فعلی تست کن')}
      </button>

      <button className="btn block" type="button"
        disabled={dirty.length === 0 || save.isPending}
        onClick={() => { setError(null); save.mutate(); }}>
        {save.isPending ? '…' : 'ثبت در صف'}
      </button>
    </>
  );
}
